import type { IResultstash, JsonQuery } from "@mne-core/api";
import { QueryController, createHeaders, fetchJsonQuery } from "@mne-core/api";

import AuthenticationURI from "./AuthenticationURIBuilder";

export interface IPrepareWebAuthnAuthenticationCriteria {
  userName: string; // Username of user preparing to authenticate
  domain: string; // User domain
  password: string; // Static Argus-password for user
}

export interface IPrepareWebAuthnAuthenticationOpts extends RequestInit {
  apiUrl: string;
  apikey?: string;
  ctrl?: QueryController;
}

export interface IPrepareWebAuthnAuthenticationResponse {
  challengeID: string; // ID of the challenge returned in this response
  challenge: string; // The actual challenge, which will be embedded in the webAuthn clientDataJSON
  permittedWebAuthnKeyIDs: Array<string>; // A list of WebAuthn key IDs which are permitted to use
  timeout: number; // The timeout to use when waiting for the user
}

export const createRequest = (
  criteria: IPrepareWebAuthnAuthenticationCriteria,
  opts: IPrepareWebAuthnAuthenticationOpts,
): Request => {
  const { apiUrl, apikey, ...restOpts } = opts;
  const uri = new AuthenticationURI()
    .setApiUrl(apiUrl)
    .setSegment(["signature", "webauthn", "authentication", "challenge"])
    .build();

  const headers = createHeaders({
    /* If the apikey is something else then undefined its added */
    "ARGUS-API-KEY": apikey,
    Accept: "application/json",
    "Content-Type": "application/json",
  });

  let localRequestOpts: RequestInit = {
    body: JSON.stringify(criteria),
    headers,
    method: "POST",
    mode: "cors",
  };

  if (!apikey) {
    /**
     * Only add cookies if no apikey is provided
     */
    localRequestOpts = {
      ...localRequestOpts,
      credentials: "include",
    };
  }

  return new Request(String(uri), {
    ...localRequestOpts,
    ...restOpts,
  });
};

async function prepareWebauthnAuthentication(
  params: IPrepareWebAuthnAuthenticationCriteria,
  opts: IPrepareWebAuthnAuthenticationOpts,
): Promise<JsonQuery<IResultstash<IPrepareWebAuthnAuthenticationResponse>>> {
  return await fetchJsonQuery<IResultstash<IPrepareWebAuthnAuthenticationResponse>>(createRequest(params, opts), {
    ctrl: opts.ctrl || new QueryController(),
  });
}

export default prepareWebauthnAuthentication;
